import {
  DEVICE_CONNECTION_STATUS,
  DEVICE_EVENT_TYPES,
  DEVICE_TYPES,
  EVENT_TYPES,
  MODEL_TO_VARIANT,
  SENSOR_BATTERY_THRESHOLDS,
  SENSOR_VARIANTS,
  SMART_PLUG_UNITS,
} from '@/constants/device';
import {
  AppStorageKeys,
  UserPermissionLevel,
  UserRoles,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { getDayLabel, getTimeZoneFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { get } from 'lodash';
import moment from 'moment-timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Reads a positive "number of days" setting from the runtime env vars (injected by server.js).
 * Missing, zero, negative or non-numeric values fall back to `defaultDays`.
 */
export const getEnvNumberOfDays = (envKey, defaultDays) => {
  const configured = Number(SessionStorage.get(AppStorageKeys.envVars)?.[envKey]);
  return Number.isFinite(configured) && configured > 0 ? configured : defaultDays;
};

export function celsiusToFahrenheit(celsius) {
  return (celsius * 9) / 5 + 32;
}

export function fahrenheitToCelsius(fahrenheit) {
  return ((fahrenheit - 32) * 5) / 9;
}

export function kilogramsToPounds(kilograms) {
  return kilograms * 2.20462; // 1 kilogram = 2.20462 pounds
}

export function poundsToKilograms(pounds) {
  return pounds / 2.20462; // 1 pound = 0.453592 kilograms
}

export function mgTommol(mg) {
  // metric to imperial
  return mg * 0.0555;
}

export function mmolTomg(mmol) {
  // metric to imperial
  return mmol * 18.018;
}

export const checkIfTVSourceEditable = (userRole, hubRecord) => {
  if (userRole === UserPermissionLevel.staffAdmin) return true;
  if (hubRecord?.rolename === UserRoles.manage) {
    return true;
  }
  return false;
};

/**
 * Detects the sensor variant based on device type and model name.
 * Supports sensors with the same devicetype but different features (e.g., occupancy_sensor with motion vs incontinence)
 * @param {Object} sensor - The sensor object with devicetype and modelname properties
 * @returns {string|null} The sensor variant or null if not applicable
 */
export const getSensorVariant = (sensor) => {
  if (!sensor) return null;

  const devicetype = get(sensor, 'devicetype');
  const rawModelName = get(sensor, 'modelname') || get(sensor, 'modelnumber') || '';
  const modelname = String(rawModelName).toUpperCase();

  // For occupancy_sensor, differentiate by modelname
  if (devicetype === DEVICE_TYPES.OCCUPANCY_SENSOR) {
    return MODEL_TO_VARIANT[modelname] || SENSOR_VARIANTS.OCCUPANCY_MOTION;
  }

  return null;
};

/**
 * Returns event types to use for latest-updates API payload.
 * Occupancy and incontinence sensors must send only one event type each.
 */
export const getLatestUpdateEventTypes = (sensor) => {
  const devicetype = get(sensor, 'devicetype');

  if (devicetype !== DEVICE_TYPES.OCCUPANCY_SENSOR) {
    return DEVICE_EVENT_TYPES[devicetype] || [];
  }

  const variant = getSensorVariant(sensor);
  return variant === SENSOR_VARIANTS.INCONTINENCE
    ? [EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_UPDATE_OCCUPANCY_SENSOR_WET]
    : [EVENT_TYPES.OCCUPANCY_SENSOR.ACCESSORY_STATE_OCCUPANCY_SENSOR_ACTIVE];
};

export const isStaffAdmin = (currentUser) =>
  currentUser?.permission_level === UserPermissionLevel.staffAdmin;

export const roundDecimalValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number' && !Number.isInteger(value)) {
    const roundedValue = Math.round(value * 100) / 100; // round to 2 decimal places
    return roundedValue.toString().length > 5
      ? roundedValue.toFixed(1).slice(0, 5)
      : roundedValue.toFixed(1).toString();
  } else {
    return value;
  }
};

export const getLocaleTime = (date, locale) => {
  const inputDate = moment(date, 'DD/MM/YYYY');
  return inputDate.locale(locale).format('L');
};

export const getFormatTimeLocale = (date, locale) => {
  return moment(date, 'ddd MM/DD').locale(locale).format('L');
};

export const getStartTime = ({ selectedRange, userTimezone }) => {
  if (selectedRange === 'today') {
    return moment().tz(userTimezone).subtract(24, 'hour').clone().toDate();
  } else if (selectedRange === 'lastweek') {
    return moment()
      .tz(userTimezone)
      .subtract(24 * 7, 'hour')
      .clone()
      .toDate();
  } else if (selectedRange === 'all') {
    return moment().tz(userTimezone).startOf('day').subtract(90, 'days').clone().toDate();
  } else {
    return moment().tz(userTimezone).startOf('day').subtract(1, 'month').clone().toDate();
  }
};

export const getDayLabelOrDate = (datestr, timeZone, locale) => {
  if (!datestr) return '-';
  const string = getDayLabel(datestr, timeZone);
  if (string !== '') return string;
  const dateInZone = dayjs.utc(datestr).tz(timeZone);
  return getLocaleTime(dateInZone.format('DD/MM/YYYY'), locale);
};

export const getBatteryLevelClassName = ({ deviceBatteryLevel, deviceSetting }) => {
  if (deviceBatteryLevel <= deviceSetting.lowBatteryThreshold) {
    return 'low-battery ';
  } else if (deviceBatteryLevel <= deviceSetting.mediumBatteryThreshold) {
    return 'medium-battery ';
  } else if (deviceBatteryLevel > deviceSetting.fullBatteryThreshold) {
    return 'full-battery ';
  }
};

export const getStatusClassNames = (
  sensor,
  connectedProp = 'deviceconnectstatus',
  pairedProp = 'paired_status'
) => {
  let classes = '';
  const connectedValue = get(sensor, connectedProp, '');
  const pairedValue = get(sensor, pairedProp, '');
  if (
    (connectedValue === DEVICE_CONNECTION_STATUS.CONNECTED || connectedValue === true) &&
    (pairedValue === DEVICE_CONNECTION_STATUS.PAIRED || pairedValue === true)
  ) {
    classes += 'connected ';
  } else if (
    connectedValue === DEVICE_CONNECTION_STATUS.DISCONNECTED ||
    connectedValue === false ||
    pairedValue === DEVICE_CONNECTION_STATUS.UNPAIRED ||
    pairedValue === false
  ) {
    classes += 'disconnected ';
  }
  return classes;
};

export const getStatusClassNamesFromSseEvent = (sensor, connectedProp = 'deviceconnectstatus') => {
  let classes = '';
  const connectedValue = get(sensor, connectedProp, '');
  if (connectedValue === DEVICE_CONNECTION_STATUS.CONNECTED || connectedValue === true) {
    classes += 'connected ';
  } else if (connectedValue === DEVICE_CONNECTION_STATUS.DISCONNECTED || connectedValue === false) {
    classes += 'disconnected ';
  }
  return classes;
};

export const getStatusBatteryClassNames = (sensor) => {
  let classes = '';
  classes += getStatusClassNames(sensor);
  const batteryLevelClass = getBatteryLevelClassName({
    deviceBatteryLevel: sensor?.device_battery_level,
    deviceSetting: SENSOR_BATTERY_THRESHOLDS,
  });
  return classes + batteryLevelClass;
};

export const getDatelabel = (lastEventData, timezone, loginUserLocale, trans) => {
  const label = getDayLabelOrDate(
    lastEventData?.utc_timestamp_inms,
    getTimeZoneFormat(timezone),
    loginUserLocale
  );
  return ['Today', 'Yesterday'].includes(label) ? trans(label.toUpperCase()) : label;
};

export const formatEnergy = (wh) => {
  if (wh == null || isNaN(wh)) return '-';
  // Convert to kWh when >=1000 Wh, rounded to nearest integer
  if (wh >= 1000) return Math.round(wh / 1000);
  // Otherwise return whole Wh
  return Math.round(wh);
};

export const formatEnergyUnit = (wh) => {
  if (wh >= 1000) return SMART_PLUG_UNITS.KWH;
  return SMART_PLUG_UNITS.WH;
};

export const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
